import React, { useRef, useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';

/**
 * "Last updated by X on ..." strip for the global form-config pages, plus the
 * two small bits of save handling every Configure page repeats.
 */
export const ConfigMeta = ({ meta }) => {
    if (!meta?.modifiedOn) return null;
    return (
        <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
            Last updated{meta.modifiedByName ? ` by ${meta.modifiedByName}` : ''} on{' '}
            {moment(meta.modifiedOn).format('DD-MM-YYYY HH:mm')}
        </p>
    );
};

/**
 * First active section with no item that has a non-empty label, or null if all are valid.
 * A section with no labelled items has nothing to save the API can point to, so it
 * silently disappears on the next load - catch it before submit instead. Sections marked
 * inactive (deleted) are exempt - they're being removed, not saved.
 */
export const findEmptySection = (sections) =>
    sections.find((section) => section.active !== false && !section.items.some((item) => item.label && item.label.trim() !== '')) || null;

/** True when the save failed because someone else saved first. */
export const isStaleConfigError = (error) =>
    error?.code === 'STALE_CONFIG' || /changed since you loaded it/i.test(error?.message || error || '');

/**
 * Toasts the outcome of a config save. Items still referenced by a submission
 * cannot be hard-deleted, so the API deactivates them instead and reports them
 * back as skippedIds - say so rather than silently doing something else.
 */
export const toastSaveResult = (result) => {
    const skipped = result?.skippedIds?.length || 0;
    toast.success(result?.message || 'Configuration saved.');
    if (skipped > 0) {
        toast.info(`${skipped} item(s) are used by existing submissions - deactivated instead of deleted.`);
    }
};

/** Trash control that hard-deletes a saved config item (or drops an unsaved row). */
export const DeleteItemButton = ({ onClick, title = 'Delete permanently' }) => (
    <button
        type="button"
        className="btn btn-sm btn-outline-danger ms-2"
        title={title}
        onClick={onClick}
    >
        <i className="bx bx-trash" />
    </button>
);

/** Undo or redo the most recent hard-delete action in a form builder. */
export const useDeleteHistory = ({ setState, onDeleteSavedItem, onRestoreSavedItem }) => {
    const undoStack = useRef([]);
    const redoStack = useRef([]);
    const [availability, setAvailability] = useState({ canUndo: false, canRedo: false });

    const updateAvailability = () => {
        setAvailability({
            canUndo: undoStack.current.length > 0,
            canRedo: redoStack.current.length > 0,
        });
    };

    const recordDelete = (entry) => {
        undoStack.current.push(entry);
        redoStack.current = [];
        updateAvailability();
    };

    const applyEntry = (entry, restore) => {
        setState((previous) => {
            const updated = [...previous];

            if (entry.section) {
                let sectionIndex = updated.findIndex((section) => section === entry.section);
                if (restore && sectionIndex === -1) {
                    sectionIndex = Math.min(entry.sectionIndex, updated.length);
                    updated.splice(sectionIndex, 0, entry.section);
                }
                if (sectionIndex === -1) return previous;

                const section = updated[sectionIndex];
                const items = [...section.items];
                if (restore) {
                    if (!items.includes(entry.item)) {
                        items.splice(Math.min(entry.itemIndex, items.length), 0, entry.item);
                    }
                } else {
                    const itemIndex = items.indexOf(entry.item);
                    if (itemIndex === -1) return previous;
                    items.splice(itemIndex, 1);
                }
                section.items = items;
            } else if (restore) {
                if (!updated.includes(entry.item)) {
                    updated.splice(Math.min(entry.itemIndex, updated.length), 0, entry.item);
                }
            } else {
                const itemIndex = updated.indexOf(entry.item);
                if (itemIndex === -1) return previous;
                updated.splice(itemIndex, 1);
            }

            return updated;
        });

        if (entry.item?.id) {
            if (restore && onRestoreSavedItem) onRestoreSavedItem(entry.item.id);
            if (!restore && onDeleteSavedItem) onDeleteSavedItem(entry.item.id);
        }
    };

    const undo = () => {
        const entry = undoStack.current.pop();
        if (!entry) return;
        applyEntry(entry, true);
        redoStack.current.push(entry);
        updateAvailability();
    };

    const redo = () => {
        const entry = redoStack.current.pop();
        if (!entry) return;
        applyEntry(entry, false);
        undoStack.current.push(entry);
        updateAvailability();
    };

    return { ...availability, recordDelete, undo, redo };
};

export const DeleteHistoryControls = ({ canUndo, canRedo, onUndo, onRedo }) => (
    <div className="d-flex justify-content-end gap-2 mb-2" aria-label="Delete history controls">
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onUndo} disabled={!canUndo}>
            Undo Delete
        </button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onRedo} disabled={!canRedo}>
            Redo Delete
        </button>
    </div>
);
